import { useMemo, useState } from 'react';
import { CardRecord, WritingContent } from '../../db/indexedDb';
import { placeholderForIndex } from './cloze';

export interface WritingClozeResult {
  answers: string[];
  inputs: string[];
  accuracy: number; // 0-100
  evaluatedAt: number;
}

type Props = {
  card: CardRecord;
  onEvaluated?: (result: WritingClozeResult) => void;
};

const normalize = (value: string) => value.trim().toLowerCase();

const buildTemplateParts = (template: string, blanks: number) => {
  const parts: Array<{ type: 'text' | 'blank'; value: string; index?: number }> = [];
  if (!template || blanks === 0) {
    parts.push({ type: 'text', value: template || '' });
    return parts;
  }

  const regex = /\{\{blank_(\d+)\}\}/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(template)) !== null) {
    const [placeholder, slot] = match;
    const start = match.index;
    if (start > lastIndex) {
      parts.push({ type: 'text', value: template.slice(lastIndex, start) });
    }
    const idx = Number.parseInt(slot, 10);
    parts.push({ type: 'blank', value: placeholder, index: Number.isFinite(idx) ? idx : undefined });
    lastIndex = start + placeholder.length;
  }

  if (lastIndex < template.length) {
    parts.push({ type: 'text', value: template.slice(lastIndex) });
  }

  return parts;
};

function WritingClozeCard({ card, onEvaluated }: Props) {
  const content = (card.content ?? {}) as WritingContent;
  const clozeTemplate =
    card.writingMeta?.cloze?.template ?? content.clozeTemplate ?? content.reference ?? '';
  const answers =
    card.writingMeta?.cloze?.answers ?? content.clozeAnswers ?? [];
  const cnHint = card.writingMeta?.cnHint ?? content.prompt ?? '写作填空';
  const reference = card.writingMeta?.cloze?.reference ?? content.reference ?? '';

  const blanks = Math.max(answers.length, 0);
  const [inputs, setInputs] = useState<string[]>(() => Array.from({ length: blanks }, () => ''));
  const [result, setResult] = useState<WritingClozeResult | null>(null);

  const parts = useMemo(() => buildTemplateParts(clozeTemplate, blanks), [clozeTemplate, blanks]);

  const handleChange = (index: number, value: string) => {
    setInputs((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const evaluate = () => {
    if (!answers.length) return;
    const correct = answers.reduce((acc, answer, idx) => {
      return acc + (normalize(inputs[idx] ?? '') === normalize(answer) ? 1 : 0);
    }, 0);
    const accuracy = Math.round((correct / answers.length) * 100);
    const payload: WritingClozeResult = {
      answers,
      inputs,
      accuracy,
      evaluatedAt: Date.now(),
    };
    setResult(payload);
    onEvaluated?.(payload);
  };

  const fillWithAnswer = (idx: number) => {
    handleChange(idx, answers[idx] ?? '');
  };

  const placeholder = (index: number) => placeholderForIndex(index);

  return (
    <div className="practice-card writing-cloze-compact">
      {/* Header */}
      <div className="practice-card-header">
        <h3 className="practice-question">{cnHint}</h3>
        <span className="pill">填空</span>
      </div>

      {/* Cloze 填空区 */}
      <div className="cloze-body">
        <div className="cloze-text">
          {parts.map((part, idx) =>
            part.type === 'blank' && typeof part.index === 'number' ? (
              <span className="cloze-blank-wrap" key={`blank-${idx}`}>
                <input
                  className="cloze-input"
                  aria-label={`空 ${part.index + 1}`}
                  value={inputs[part.index] ?? ''}
                  onChange={(e) => handleChange(part.index as number, e.target.value)}
                  placeholder={`___`}
                />
              </span>
            ) : (
              <span key={`text-${idx}`}>{part.value}</span>
            )
          )}
        </div>

        {/* 判分按钮 */}
        <div className="cloze-submit">
          <button className="cloze-btn" type="button" onClick={evaluate} disabled={!answers.length}>
            判分
          </button>
        </div>
      </div>

      {/* 结果 */}
      {result && (
        <div className="cloze-result">
          <span className="cloze-score">{result.accuracy}%</span>
          <div className="cloze-answers">
            {answers.map((ans, i) => (
              <span 
                key={i} 
                className={`cloze-answer ${normalize(inputs[i] ?? '') === normalize(ans) ? 'correct' : 'wrong'}`}
              >
                {ans}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default WritingClozeCard;
